import { http, pollStatus } from '@pendo/http';
import isString from 'lodash/isString';

import { EDITOR_TYPES } from '@pendo/services/Constants.js';
import { TaskList } from '@pendo/services/TaskList';

export function loadGuides ({ expandKey, includeArchived, includeDeleted = false, includeMultiApp = true, appId }) {
    let url = `/api/s/_SID_/guide?includeArchived=${includeArchived}&includeDeleted=${includeDeleted}&includeMultiApp=${includeMultiApp}`;
    if (expandKey) {
        url += `&expand=${expandKey}`;
    }
    if (appId) {
        url += `&appId=${appId}`;
    }

    return http.get(url);
}

export function loadGuide ({ id, tag }) {
    let url = `/api/s/_SID_/guide/${id}`;
    if (tag) {
        url += `/tag/${tag}`;
    }

    return http.get(url);
}

export function loadGuideTranslations (guideId, langCode) {
    return http.get(`/api/s/_SID_/guide/${guideId}/localization/translations/${langCode}`).then((res) => res.data);
}

export async function loadGuidesForTaskList (guide, guidesMap) {
    const taskList = TaskList.getTaskListUsingDomJSON(guide);
    if (!taskList) return [];

    const guidesToLoad = [];
    const guides = [];

    taskList.templateChildren.forEach((child) => {
        const guideFromMap = guidesMap[child.id];
        if (!guideFromMap) {
            guidesToLoad.push(loadGuide({ id: child.id }));
        } else {
            guides.push(guideFromMap);
        }
    });

    const responses = await Promise.all(guidesToLoad);
    responses.forEach((response) => {
        guides.push(response.data);
    });

    return guides;
}

export function createGuideTranslations (guideId, langCode) {
    return http.post(`/api/s/_SID_/guide/${guideId}/localization/translations/${langCode}`).then((res) => res.data);
}

export function saveGuideTranslations (guideId, langCode, translations) {
    return http
        .put(`/api/s/_SID_/guide/${guideId}/localization/translations/${langCode}`, translations)
        .then((res) => res.data);
}

export async function bulkTranslateGuide (guideId, langCodes) {
    const res = await http
        .post(`/api/s/_SID_/guide/${guideId}/localization/translations/bulk`, { langCodes });

    return res.data;
}

export function pollBulkTranslateGuideStatus (guideId, shouldStopPolling) {
    const endpoint = `/api/s/_SID_/guide/${guideId}/localization/translations/bulk/status`;

    return pollStatus(endpoint, shouldStopPolling, 3);
}

export function saveGuide ({ guide, preserveUpdateInfo = false, isOrgResource = false }) {
    if (!guide.editorType || guide.editorType.trim() === '') guide.editorType = EDITOR_TYPES.ENGAGE_UI;

    if (guide.steps) {
        guide.steps.forEach((step) => {
            if (step.buildingBlocks && !isString(step.buildingBlocks)) {
                step.buildingBlocks = JSON.stringify(step.buildingBlocks);
            }
        });
    }

    let path = `/api/s/_SID_/guide/${guide.id}?preserveUpdateInfo=${preserveUpdateInfo}`;
    if (isOrgResource) {
        path = `/api/s/_SID_/guide/${guide.id}?preserveUpdateInfo=${preserveUpdateInfo}&orgResource=true`;
    }

    return http.put(path, guide);
}

export function createGuideFromHtml ({ rawHtmls, appId, theme, guide }) {
    return http.post('/api/s/_SID_/guide/htmlToGuide', { rawHtmls, appId, theme, guide });
}

export function deleteGuide ({ id }) {
    return http.delete(`/api/s/_SID_/guide/${id}`);
}

export function bulkPatchGuides ({ requests }) {
    return http.patch('/api/s/_SID_/guide/bulk', { requests });
}

export function publishGuide ({ guide }) {
    const { id, stableVersionId } = guide;

    return http.put(`/api/s/_SID_/versiontag/production/guide/${id}`, { stableVersionId });
}

export function saveStep ({ guide, step }) {
    return http.put(`/api/s/_SID_/guide/${guide.id}/step/${step.id}`, step);
}

export function deleteStep ({ guide, step }) {
    return http.delete(`/api/s/_SID_/guide/${guide.id}/step/${step.id}`);
}

export function isNpsGuide (guide) {
    return TaskList.isNpsGuide(guide);
}

export function isIdeaPollGuide (guide) {
    return TaskList.isIdeaPollGuide(guide);
}

export function isSurveyGuide (guide) {
    return !!guide?.attributes?.surveyId;
}

export function saveGuideExperiment ({ guideId, experiment }) {
    return http.put(`/api/s/_SID_/guide/${guideId}/experiment`, experiment);
}

export function deleteGuideExperiment ({ guideId }) {
    return http.delete(`/api/s/_SID_/guide/${guideId}/experiment`);
}

export async function saveGuideRedisplay ({ guideId, redisplay }) {
    return http.put(`/api/s/_SID_/guide/${guideId}/redisplay`, redisplay);
}

export async function deleteGuideRedisplay ({ guideId }) {
    return http.delete(`/api/s/_SID_/guide/${guideId}/redisplay`);
}

export async function getClassicDesignerTemplates () {
    return http.get('/api/s/_SID_/guidetemplate/');
}

export function cloneGuideViaEndpoint (guideId, options = {}) {
    return http.post(`/api/s/_SID_/guide/${guideId}/clone`, options).then((res) => res.data);
}

export function setGuideDNT (guideId, isDNT) {
    return http.put(`/api/s/_SID_/guide/${guideId}/dnt`, { IsDNT: isDNT });
}
